/**
 * The named run budgets, matching the backend's `ScrapeProfile`.
 *
 * A preset bundles the three run settings — posts per run, run timeout, memory
 * — because they have to agree with each other: every post in a run shares that
 * run's timeout, so a heavy account needs fewer posts per run and a longer one.
 * Choosing a mode of use instead of three numbers is what stops that pairing
 * being got wrong.
 *
 * Shared, and with labels, because the two places that offer this were each
 * rendering the wire values (`batch_min`) straight into a dropdown.
 */
export type ScrapeProfileOption = {
    /** The string the backend parses. Underscores, not the CLI's hyphens. */
    wireValue: string
    label: string
    hint: string
}

/**
 * Day-to-day monitoring: a narrow window where each post has few new comments,
 * so many posts fit in one quick run. The backend's default when no profile is
 * sent, and only ever the right answer for a recurring monitor.
 */
export const STANDARD_PROFILE: ScrapeProfileOption = {
    wireValue: 'daily',
    label: 'Standard — daily monitoring',
    hint: 'A narrow window, so many posts fit in one quick run.'
}

/**
 * The backfill tiers, in the order they should be offered.
 *
 * Deliberately without Standard: a historic fetch on the daily budget times out
 * partway through the range and returns what it managed, which looks like a
 * successful run rather than a truncated one.
 */
export const BACKFILL_TIERS: ScrapeProfileOption[] = [
    {
        wireValue: 'batch_min',
        label: 'Low',
        hint: 'Quiet accounts, or a few weeks.'
    },
    {
        wireValue: 'batch_medium',
        label: 'Medium',
        hint: 'Most backfills.'
    },
    {
        wireValue: 'batch_high',
        label: 'High',
        hint: 'Very busy accounts, or months at a time. Costs the most.'
    }
]

/** Everything a schedule can be created with: a monitor may want either. */
export const ALL_PROFILES: ScrapeProfileOption[] = [STANDARD_PROFILE, ...BACKFILL_TIERS]

/** What a historic fetch starts on, and what the mutation must always be sent. */
export const DEFAULT_BACKFILL_TIER = 'batch_medium'

export function hintFor(wireValue: string): string {
    return ALL_PROFILES.find((profile) => profile.wireValue === wireValue)?.hint ?? ''
}
